import type { YearlyMileageOptionDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { getCarYearlyMileagesWithSuggestionSelector } from '../recoil/selectors/getCarYearlyMileagesWithSuggestionSelector';
import type { MVPYearlyMileage_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { OnlineSales } from 'gw-edge-client';
import { jobInfoAtom } from '../recoil/atoms/jobInfoAtom';
import { opcInfoAtom } from '../recoil/atoms/opcInfoAtom';
import { isAxiosError } from 'axios';
import { getOPCCoverableAttributeSelector } from '../recoil/selectors/getOPCCoverableAttributeSelector';

export const useYearlyMileagesAndOdometerReading = (): {
    options: YearlyMileageOptionDTO[];
    suggestedMileage: MVPYearlyMileage_Top;
    setSuggestedMileage: React.Dispatch<React.SetStateAction<MVPYearlyMileage_Top>>;
    canSaveOdometerAndMileage: (
        odometerValue: string,
        yearlyMileage: MVPYearlyMileage_Top
    ) => Promise<string>;
    updateAdditionalAttributeOPC: (
        yearlyMileage: MVPYearlyMileage_Top,
        odometerValue: string
    ) => Promise<string>;
} => {
    const { options, suggestedValue } = useRecoilValue(getCarYearlyMileagesWithSuggestionSelector);
    const currentYearlyMileage = useRecoilValue(getOPCCoverableAttributeSelector)?.car
        ?.yearlyMileage;
    const [suggestedMileage, setSuggestedMileage] = useState<MVPYearlyMileage_Top>(
        currentYearlyMileage ?? suggestedValue
    );
    const { jobNumber, sessionId } = useRecoilValue(jobInfoAtom);
    const { coverableId } = useRecoilValue(opcInfoAtom);

    const canSaveOdometerAndMileage = async (
        odometerValue: string,
        yearlyMileage: MVPYearlyMileage_Top
    ): Promise<string> => {
        try {
            await OnlineSales.validateCarOdometerReading(
                jobNumber,
                sessionId,
                coverableId,
                odometerValue,
                yearlyMileage
            );
        } catch (e) {
            if (isAxiosError(e)) {
                return e.response?.data.error.pureMessage_Top as string;
            }
            throw e;
        }
        return '';
    };

    const updateAdditionalAttributeOPC = async (
        yearlyMileage: MVPYearlyMileage_Top,
        odometerReading: string
    ): Promise<string> => {
        try {
            await OnlineSales.updateCarOdometerReading(
                jobNumber,
                sessionId,
                coverableId,
                yearlyMileage,
                odometerReading
            );
        } catch (e) {
            if (isAxiosError(e)) {
                return e.response?.data.error.pureMessage_Top as string;
            }
            throw e;
        }
        return '';
    };

    return {
        options,
        suggestedMileage,
        setSuggestedMileage,
        canSaveOdometerAndMileage,
        updateAdditionalAttributeOPC
    };
};
